import {
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Button,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../../../context";
import Loading from "./skeletonLoading/Loading";
import useFetchObjects from "../../../../api/useFetchObjects";
import useUpdate from "../../../../api/useUpdate";
import NoRecordMessage from "../../../../Components/GeneralComponents/noRecordMessage/NoRecordMessage";
import EditPatientDialog from "./EditPatientDialog";
import { useTranslation } from "react-i18next";

const searchStyle = {
  padding: "14px",
  borderRadius: "17px",
  backgroundColor: "#f1f3f5",
  border: "1px solid #cdd0d4",
  width: "100%",
  marginBottom: "15px",
  ":focus": { outline: "none", borderColor: "#007bff" },
};

const tableStyle = {
  textAlign: "center",
  padding: "10px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "150px", // Set max width for consistent column size
};

const PatientList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [pagination, setPagination] = useState({
    next: null,
    previous: null,
  });

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["patients", page, rowsPerPage],
    `patients/?page=${page + 1}&page_size=${rowsPerPage}&search=${search}`,
    token,
  );

  const { handleUpdate } = useUpdate("patients", token);

  useEffect(() => {
    if (data) {
      setPagination(data.links);
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, [refetch, search]);

  const handleEditClick = (patient) => {
    setSelectedPatient(patient);
    setEditOpen(true);
  };

  const handleSaveEdit = (updatedPatient) => {
    handleUpdate(updatedPatient.id, updatedPatient);
    setEditOpen(false);
    refetch();
  };

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return <Typography color="error">{t("reception.errorLoading")}</Typography>;
  }

  return (
    <Grid container justifyContent="center" mb={4}>
      <Grid
        item
        xs={12}
        md={20}
        mt={2}
        sx={{
          backgroundColor: "white",
          overflowX: "auto",
          boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
        }}
      >
        <Box
          padding="20px"
          borderBottom="1px solid #e0e0e0"
          backgroundColor="#fafafa"
        >
          <Typography variant="h5" fontWeight="bold">
            {t("reception.patientList")}
          </Typography>
          <Typography color="text.secondary" mt={1} mb={1}>
            {data.count} {t("reception.patients")}
          </Typography>
          <input
            type="text"
            style={searchStyle}
            placeholder={t("reception.search")}
            value={search}
            onChange={handleSearchChange}
          />
        </Box>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="patient table">
            <TableHead style={{ backgroundColor: "#f2f2f2" }}>
              <TableRow>
                <TableCell style={tableStyle}>{t("reception.id")}</TableCell>
                <TableCell style={tableStyle}>
                  {t("reception.firstname")}
                </TableCell>
                <TableCell style={tableStyle}>
                  {t("reception.lastname")}
                </TableCell>
                <TableCell style={tableStyle}>
                  {t("reception.gender")}
                </TableCell>
                <TableCell style={tableStyle}>{t("reception.age")}</TableCell>
                <TableCell style={tableStyle}>{t("reception.phone")}</TableCell>
                <TableCell style={tableStyle}>
                  {t("reception.bloodType")}
                </TableCell>
                <TableCell style={tableStyle}>
                  {t("reception.passport")}
                </TableCell>
                <TableCell style={tableStyle}>
                  {t("reception.address")}
                </TableCell>
                <TableCell style={tableStyle}>
                  {t("reception.dosiaFileId")}
                </TableCell>
                <TableCell style={tableStyle}>
                  {t("reception.action")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.results.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} style={{ textAlign: "center" }}>
                    <NoRecordMessage message={t("reception.noRecords")} />
                  </TableCell>
                </TableRow>
              ) : (
                data.results.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#f9f9f9",
                      },
                    }}
                  >
                    <TableCell style={tableStyle}>{item.id}</TableCell>
                    <TableCell style={tableStyle}>
                      <Tooltip title={item.firstname}>
                        <span>{item.firstname}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell style={tableStyle}>
                      <Tooltip title={item.lastname}>
                        <span>{item.lastname}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell style={tableStyle}>
                      {item.gender_display}
                    </TableCell>
                    <TableCell style={tableStyle}>{item.age}</TableCell>
                    <TableCell style={tableStyle}>{item.phone}</TableCell>
                    <TableCell style={tableStyle}>
                      {item.blood_type_display}
                    </TableCell>
                    <TableCell style={tableStyle}>
                      <Tooltip
                        title={item.passport || t("reception.notProvided")}
                      >
                        <span>
                          {item.passport || t("reception.notProvided")}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell style={tableStyle}>
                      <Tooltip
                        title={item.address || t("reception.notProvided")}
                      >
                        <span>
                          {item.address || t("reception.notProvided")}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell style={tableStyle}>
                      {item.dosia_file_id || t("reception.notProvided")}
                    </TableCell>
                    <TableCell style={tableStyle}>
                      <IconButton
                        color="primary"
                        onClick={() => handleEditClick(item)}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          padding="10px 20px"
        >
          <Button
            variant="outlined"
            disabled={!pagination.previous}
            onClick={() => setPage((prev) => prev - 1)}
          >
            {t("reception.previous")}
          </Button>
          <Typography>
            {t("reception.page")} {page + 1}
          </Typography>
          <Button
            variant="outlined"
            disabled={!pagination.next}
            onClick={() => setPage((prev) => prev + 1)}
          >
            {t("reception.next")}
          </Button>
        </Box>
        {selectedPatient && (
          <EditPatientDialog
            open={editOpen}
            onClose={() => setEditOpen(false)}
            patient={selectedPatient}
            onSave={handleSaveEdit}
            token={token}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default PatientList;
